import { Injectable, Inject, Optional } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { get } from 'lodash-es';
import { PlatformService } from '@core/services/util/platform.service';
import { REQUEST } from '@app/shared/token/tokens';


@Injectable({
    providedIn: 'root'
})
export class CookieService {

    constructor(
        @Optional() @Inject(REQUEST) private request: any, // optional because of multiple platform
        @Inject(DOCUMENT) private document: any,
        private platformService: PlatformService
    ) {
    }

    get source() {
        if (this.platformService.isServer()) {
            return get(this.request, 'headers.cookie', null);
        }

        if (this.platformService.isBrowser()) {
            return get(this.document, 'cookie', null);
        }

        return null;
    }

    /**
     * @param name Cookie name
     * @returns {boolean}
     */
    check(key: string): boolean {
        const encodedKey = encodeURIComponent(key);
        const regExp = this.getCookieRegExp(encodedKey);
        return regExp.test(this.source);
    }

    /**
     * @param name Cookie name
     * @returns {any}
     */
    get(key: string): string {
        if (!this.check(key)) {
            return undefined;
        }

        const encodedKey = encodeURIComponent(key);
        const regExp = this.getCookieRegExp(encodedKey);
        const result = regExp.exec(this.source);
        return result && decodeURIComponent(result[1]);
    }

    /**
     * @returns {}
     */
    getAll() {
        const cookies = {};
        if (this.source) {
            const rawCookies = this.source.split(';');
            rawCookies.forEach(rawCookie => {
                const [rawKey, rawValue] = rawCookie.split('=');
                cookies[decodeURIComponent(rawKey.replace(/^ /, ''))] = decodeURIComponent(rawValue);
            });
        }
        return cookies;
    }

    /**
     * @param name    Cookie name
     * @param value   Cookie value
     * @param expires Number of days until the cookies expires or an actual `Date`
     * @param path    Cookie path
     * @param domain  Cookie domain
     * @param secure  Secure flag
     */
    set(key: string, value: string, expires?: number | Date, path?: string, domain?: string, secure?: boolean) {
        if (this.platformService.isServer()) {
            return false; // cannot set cookie on server
        }

        const cookieString = [encodeURIComponent(key) + '=' + encodeURIComponent(value)];
        if (expires) {
            if (typeof expires === 'number') {
                const dateExpires = new Date(new Date().getTime() + expires * 1000 * 60 * 60 * 24);
                cookieString.push('expires=' + dateExpires.toUTCString());
            }
            else {
                cookieString.push('expires=' + expires.toUTCString());
            }
        }
        if (domain) cookieString.push('domain=' + domain);
        if (secure) cookieString.push('secure;');

        cookieString.push('path=' + (path || '/'));
        this.document.cookie = cookieString.join(';');
    }

    /**
     * @param name   Cookie name
     * @param path   Cookie path
     * @param domain Cookie domain
     */
    remove(key: string, path?: string, domain?: string) {
        this.set(key, '', -1, path, domain);
    }

    /**
     * @param path   Cookie path
     * @param domain Cookie domain
     */
    deleteAll(path?: string, domain?: string) {
        Object.keys(this.getAll()).forEach(cookieName => {
            this.remove(cookieName, path, domain);
        });
    }

    /**
     * @param name Cookie name
     * @returns {RegExp}
     */
    private getCookieRegExp(key) {
        const escapedKey = key.replace(/([\[\]\{\}\(\)\|\=\;\+\?\,\.\*\^\$])/ig, '\\$1');
        return new RegExp(`(?:^${escapedKey}|;\\s*${escapedKey})=(.*?)(?:;|$)`, 'g');
    }
}
